/**
 * Customer disposal fees — types and pure helpers (#52, DEC-358). What the company CHARGES
 * each customer per bbl to dispose at each place; not the disposal prices it PAYS.
 *
 * The bulk actions (set for all customers, one fee for every place, copy a customer, seed
 * defaults from cost) are built here as plain import rows, so the API previews, applies and
 * audits them exactly like an uploaded file.
 */

import { parseDollarsToCents, hasSubCentPrecision } from '@lodex-pro/shared/money';
import { formatDollarsFromCents } from '@lodex-pro/shared/number-format';

/** SWD A2: WELL is a catalog disposal well this company authorized (0210). */
export type PlaceKind = 'FACILITY' | 'LOCATION' | 'WELL';
export type FeeSource = 'CUSTOMER' | 'DEFAULT' | 'NONE';

export interface DatedFee {
  readonly feeCentsPerBbl: number | null;
  readonly effectiveFrom: string;
}

export interface FeeSite {
  readonly kind: PlaceKind;
  readonly id: string;
  readonly name: string | null;
  readonly defaultFee: DatedFee | null;
  readonly nextDefault: DatedFee | null;
  readonly customerPriceCount: number;
  /** Present only for MANAGE_DISPOSAL_PRICES holders. Hundredths of a cent. */
  readonly costHundredthsCentPerBbl?: number | null;
}

export interface SitesResponse {
  readonly today: string;
  readonly sites: FeeSite[];
}

export interface SiteCustomerPrice {
  readonly haulerOperatorId: string;
  readonly customerName: string | null;
  readonly customerAbbreviation: string | null;
  readonly fee: DatedFee | null;
  readonly next: DatedFee | null;
}

export interface SiteDetailResponse {
  readonly today: string;
  readonly site: { readonly kind: PlaceKind; readonly id: string; readonly name: string };
  readonly defaultFee: DatedFee | null;
  readonly nextDefault: DatedFee | null;
  readonly customers: SiteCustomerPrice[];
}

export interface CustomerSite {
  readonly kind: PlaceKind;
  readonly id: string;
  readonly name: string | null;
  readonly feeCentsPerBbl: number | null;
  readonly source: FeeSource;
  readonly effectiveFrom: string | null;
  readonly defaultFeeCentsPerBbl: number | null;
  readonly excluded: boolean;
}

export interface CustomerFeesResponse {
  readonly day: string;
  readonly customer: {
    readonly id: string;
    readonly name: string | null;
    readonly abbreviation: string;
  };
  readonly sites: CustomerSite[];
}

export interface Customer {
  readonly id: string;
  readonly name: string | null;
  readonly abbreviation: string;
}

export interface HistoryRow {
  readonly id: string;
  readonly kind: PlaceKind;
  readonly targetId: string;
  readonly targetName: string | null;
  readonly haulerOperatorId: string | null;
  readonly customerName: string | null;
  readonly feeCentsPerBbl: number | null;
  readonly effectiveFrom: string;
  readonly notes: string | null;
  readonly importId: string | null;
  readonly createdAt: string;
  readonly createdByName: string | null;
}

export interface SetFeeBody {
  readonly target: { readonly kind: PlaceKind; readonly id: string };
  readonly haulerOperatorId: string | null;
  readonly feeCentsPerBbl: number | null;
  readonly effectiveFrom: string;
  readonly notes?: string | null;
}

/** One requested change — the same shape the file parser returns. */
export interface ImportRow {
  readonly rowNumber: number;
  readonly customerAbbreviation?: string;
  readonly siteId?: string;
  readonly site?: string;
  readonly newFee?: string;
  readonly effectiveFrom?: string;
  readonly notes?: string;
}

export type ImportSource =
  | { readonly kind: 'FILE'; readonly fileName: string; readonly fileSha256: string }
  | {
      readonly kind: 'BULK';
      readonly action: 'SET_FOR_ALL' | 'COPY_CUSTOMER' | 'SEED_FROM_COST';
      readonly label: string;
    };

export type RowStatus = 'ADD' | 'CHANGE' | 'END' | 'UNCHANGED' | 'ERROR';

export interface PreviewRow {
  readonly rowNumber: number;
  readonly status: RowStatus;
  readonly haulerOperatorId: string | null;
  readonly customerLabel: string | null;
  readonly siteKind: PlaceKind | null;
  readonly siteId: string | null;
  readonly siteName: string | null;
  readonly effectiveFrom: string | null;
  readonly before: number | null;
  readonly after: number | null;
  readonly notes: string | null;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

export interface PreviewCounts {
  readonly add: number;
  readonly change: number;
  readonly end: number;
  readonly unchanged: number;
  readonly error: number;
}

export interface PreviewResponse {
  readonly issuedAt: string;
  readonly previewToken: string;
  readonly counts: PreviewCounts;
  readonly rows: PreviewRow[];
}

/** 45 → "$0.45"; 123456 → "$1,234.56" (OPEN-198). */
export function formatFee(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return formatDollarsFromCents(Math.trunc(cents));
}

/** 45 → "0.45": the plain form the import rows carry. */
export function feeText(cents: number): string {
  return formatFee(cents).slice(1);
}

export type FeeInput =
  | { readonly ok: true; readonly cents: number }
  | { readonly ok: false; readonly message: string };

/** The fee box → whole cents, or why it can't be saved (DEC-358: whole cents only). */
export function readFeeInput(raw: string): FeeInput {
  if (hasSubCentPrecision(raw)) {
    return { ok: false, message: 'Fees are whole cents, like 0.45.' };
  }
  const r = parseDollarsToCents(raw);
  if (!r.ok) {
    return {
      ok: false,
      message: r.reason === 'blank' ? 'Enter the fee per barrel.' : 'Enter a fee like 0.45.',
    };
  }
  if (r.cents < 0) return { ok: false, message: 'A fee cannot be negative.' };
  if (r.cents > 10_000) return { ok: false, message: 'That is over $100 a barrel.' };
  return { ok: true, cents: r.cents };
}

export function siteIdOf(kind: PlaceKind, id: string): string {
  return `${kind === 'FACILITY' ? 'F' : kind === 'LOCATION' ? 'L' : 'W'}:${id}`;
}

export const SOURCE_LABELS: Record<FeeSource, string> = {
  CUSTOMER: 'Customer price',
  DEFAULT: 'Default',
  NONE: 'No fee',
};

export const STATUS_LABELS: Record<RowStatus, string> = {
  ADD: 'Add',
  CHANGE: 'Change',
  END: 'End',
  UNCHANGED: 'No change',
  ERROR: 'Error',
};

/**
 * "Set for all customers" at one place: the new default, plus (optionally) ending the
 * customer prices listed in `endFor` so those customers fall back to it.
 */
export function setForAllRows(args: {
  readonly site: { readonly kind: PlaceKind; readonly id: string };
  readonly cents: number;
  readonly endFor: readonly string[];
}): ImportRow[] {
  const siteId = siteIdOf(args.site.kind, args.site.id);
  return [
    { rowNumber: 1, customerAbbreviation: '', siteId, newFee: feeText(args.cents) },
    ...args.endFor.map((abbr, i) => ({
      rowNumber: i + 2,
      customerAbbreviation: abbr,
      siteId,
      newFee: 'DEFAULT',
    })),
  ];
}

/** "One fee at every place" for one customer. */
export function oneFeeEverywhereRows(args: {
  readonly abbreviation: string;
  readonly sites: readonly { readonly kind: PlaceKind; readonly id: string }[];
  readonly cents: number;
}): ImportRow[] {
  return args.sites.map((s, i) => ({
    rowNumber: i + 1,
    customerAbbreviation: args.abbreviation,
    siteId: siteIdOf(s.kind, s.id),
    newFee: feeText(args.cents),
  }));
}

/** Copy the source customer's OWN prices (not defaults) to another customer. */
export function copyCustomerRows(args: {
  readonly fromSites: readonly CustomerSite[];
  readonly toAbbreviation: string;
}): ImportRow[] {
  return args.fromSites
    .filter((s) => s.source === 'CUSTOMER' && s.feeCentsPerBbl !== null)
    .map((s, i) => ({
      rowNumber: i + 1,
      customerAbbreviation: args.toAbbreviation,
      siteId: siteIdOf(s.kind, s.id),
      newFee: feeText(s.feeCentsPerBbl ?? 0),
    }));
}

/** Our cost in hundredths of a cent → whole cents, half up ($0.425 → $0.43). */
export function costToCents(hundredthsCent: number): number {
  return Math.floor((hundredthsCent + 50) / 100);
}

/** A default fee at every place that has a cost and no default yet, rounded to the cent. */
export function seedFromCostRows(sites: readonly FeeSite[]): ImportRow[] {
  return sites
    .filter(
      (s) =>
        s.defaultFee === null &&
        s.costHundredthsCentPerBbl !== undefined &&
        s.costHundredthsCentPerBbl !== null,
    )
    .map((s, i) => ({
      rowNumber: i + 1,
      customerAbbreviation: '',
      siteId: siteIdOf(s.kind, s.id),
      newFee: feeText(costToCents(s.costHundredthsCentPerBbl ?? 0)),
      notes: `Seeded from our cost of $${(Number(s.costHundredthsCentPerBbl) / 10000).toFixed(4)}/bbl`,
    }));
}
